/**
 * Onboarding version constants.
 * Using numeric versions for easy comparison with < and > operators.
 */
export const ONBOARDING_VERSION = {
  V1: 1,
  V2: 2,
} as const;

/** Current onboarding version for new advisors */
export const CURRENT_ONBOARDING_VERSION = ONBOARDING_VERSION.V2;

/**
 * Parses an onboarding version string to a number.
 * Returns current version for null/undefined/invalid values (new advisors without a record yet).
 * Note: Existing advisors in the database have onboardingVersion defaulted to '1'.
 */
export function parseOnboardingVersion(
  onboardingVersion: string | number | undefined | null,
): number {
  if (onboardingVersion == null) {
    return CURRENT_ONBOARDING_VERSION;
  }
  const version =
    typeof onboardingVersion === 'number' ? onboardingVersion : parseInt(onboardingVersion, 10);
  return isNaN(version) ? CURRENT_ONBOARDING_VERSION : version;
}

/**
 * Determines if an advisor is from a legacy onboarding flow.
 *
 * An advisor is considered "legacy" if their onboardingVersion is less than
 * the current version. Legacy advisors should be exempt from new onboarding
 * flow requirements since they already went through a previous onboarding system.
 */
export function isLegacyOnboardingAdvisor(
  onboardingVersion: string | number | undefined | null,
): boolean {
  return parseOnboardingVersion(onboardingVersion) < CURRENT_ONBOARDING_VERSION;
}

/** Onboarding steps count by version */
const ONBOARDING_STEPS_BY_VERSION: Record<number, number> = {
  [ONBOARDING_VERSION.V1]: 4,
  [ONBOARDING_VERSION.V2]: 6,
};

/**
 * Returns the total number of onboarding steps for a given onboarding version.
 */
export function getOnboardingStepsCount(
  onboardingVersion: string | number | undefined | null,
): number {
  const version = parseOnboardingVersion(onboardingVersion);
  return (
    ONBOARDING_STEPS_BY_VERSION[version] ?? ONBOARDING_STEPS_BY_VERSION[CURRENT_ONBOARDING_VERSION]
  );
}
